import { useMemo, useCallback } from "react";
import { useAccount, useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { type Address, zeroAddress } from "viem";
import {
  DJNN_TOKEN_ADDRESS,
  DJNN_REWARD_ABI,
  DISTRIBUTOR_ABI,
  getRewardTokenMeta,
  type RewardTokenMeta,
} from "../config/rewards";

/* ═══════════════════════════════════════════════════════════════════════════ */
/* useRewards — reads DJNN reward slots, distributors, and pending rewards   */
/* ═══════════════════════════════════════════════════════════════════════════ */

/** Data for a single reward slot */
export interface RewardSlotData {
  slot: number;
  distributorAddress: Address;
  rewardTokenAddress: Address;
  meta: RewardTokenMeta;
  pendingReward: bigint;
  totalDistributed: bigint;
  totalDividends: bigint;
  shareholderCount: bigint;
  bps: bigint;
  totalRealised: bigint;
}

const MAX_SLOTS = 10;

export function useRewards() {
  const { address: account } = useAccount();
  const { writeContract, isPending: isWritePending, data: txHash } = useWriteContract();
  const { isLoading: isConfirming } = useWaitForTransactionReceipt({ hash: txHash });

  /* ── Step 1: Read slot count ──────────────────────────────────────────── */

  const { data: slotCountRaw, isLoading: isLoadingSlotCount } = useReadContract({
    address: DJNN_TOKEN_ADDRESS,
    abi: DJNN_REWARD_ABI,
    functionName: "rewardSlotCount",
    query: { refetchInterval: 30_000 },
  });

  const slotCount = Number(slotCountRaw ?? 0n);

  /* ── Step 2: Read DJNN balance of connected wallet ───────────────────── */

  const { data: djnnBalanceRaw, isLoading: isLoadingBalance } = useReadContract({
    address: DJNN_TOKEN_ADDRESS,
    abi: DJNN_REWARD_ABI,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: {
      enabled: !!account,
      refetchInterval: 15_000,
    },
  });

  const djnnBalance = (djnnBalanceRaw as bigint) ?? 0n;

  /* ── Step 3: Batch read per-slot data from DJNN token ────────────────── */
  /* For each slot: distributors(slot), rewardTokens(slot), rewardBps(slot) */

  const slotCalls = useMemo(() => {
    if (slotCount === 0) return [];
    const calls: {
      address: Address;
      abi: typeof DJNN_REWARD_ABI;
      functionName: string;
      args: readonly [bigint];
    }[] = [];
    for (let i = 0; i < slotCount; i++) {
      calls.push(
        { address: DJNN_TOKEN_ADDRESS, abi: DJNN_REWARD_ABI, functionName: "distributors", args: [BigInt(i)] },
        { address: DJNN_TOKEN_ADDRESS, abi: DJNN_REWARD_ABI, functionName: "rewardTokens", args: [BigInt(i)] },
        { address: DJNN_TOKEN_ADDRESS, abi: DJNN_REWARD_ABI, functionName: "rewardBps", args: [BigInt(i)] }
      );
    }
    return calls;
  }, [slotCount]);

  const { data: slotResults, isLoading: isLoadingSlots } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: slotCalls as any,
    query: {
      enabled: slotCount > 0,
      refetchInterval: 15_000,
    },
  });

  /* Parse slot results into intermediate arrays */
  const { distributorAddresses, rewardTokenAddresses, bpsValues } = useMemo(() => {
    const distributorAddresses: Address[] = [];
    const rewardTokenAddresses: Address[] = [];
    const bpsValues: bigint[] = [];

    if (!slotResults) return { distributorAddresses, rewardTokenAddresses, bpsValues };

    for (let i = 0; i < slotCount; i++) {
      const base = i * 3;
      const dist = (slotResults[base]?.result as Address | undefined) ?? zeroAddress;
      const rToken = (slotResults[base + 1]?.result as Address | undefined) ?? zeroAddress;
      const bps = (slotResults[base + 2]?.result as bigint | undefined) ?? 0n;
      distributorAddresses.push(dist);
      rewardTokenAddresses.push(rToken);
      bpsValues.push(bps);
    }

    return { distributorAddresses, rewardTokenAddresses, bpsValues };
  }, [slotResults, slotCount]);

  /* ── Step 4: Batch read from each distributor contract ────────────────── */
  /* For each active distributor: unpaidEarnings, totalDistributed, totalDividends, shareholderCount, getShareInfo */

  const distributorCalls = useMemo(() => {
    if (distributorAddresses.length === 0) return [];
    const calls: {
      address: Address;
      abi: typeof DISTRIBUTOR_ABI;
      functionName: string;
      args?: readonly unknown[];
    }[] = [];

    for (const distAddr of distributorAddresses) {
      if (distAddr === zeroAddress) {
        /* Push 5 placeholder reads that will fail gracefully — we skip them in parse */
        for (let j = 0; j < 5; j++) {
          calls.push({
            address: zeroAddress,
            abi: DISTRIBUTOR_ABI,
            functionName: "totalDistributed",
          });
        }
        continue;
      }

      calls.push(
        {
          address: distAddr,
          abi: DISTRIBUTOR_ABI,
          functionName: "unpaidEarnings",
          args: account ? [account] : [zeroAddress],
        },
        {
          address: distAddr,
          abi: DISTRIBUTOR_ABI,
          functionName: "totalDistributed",
        },
        {
          address: distAddr,
          abi: DISTRIBUTOR_ABI,
          functionName: "totalDividends",
        },
        {
          address: distAddr,
          abi: DISTRIBUTOR_ABI,
          functionName: "shareholderCount",
        },
        {
          address: distAddr,
          abi: DISTRIBUTOR_ABI,
          functionName: "getShareInfo",
          args: account ? [account] : [zeroAddress],
        }
      );
    }

    return calls;
  }, [distributorAddresses, account]);

  const { data: distributorResults, isLoading: isLoadingDistributors } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: distributorCalls as any,
    query: {
      enabled: distributorAddresses.length > 0,
      refetchInterval: 15_000,
    },
  });

  /* ── Step 5: Assemble final RewardSlotData[] ─────────────────────────── */

  const slots: RewardSlotData[] = useMemo(() => {
    if (
      distributorAddresses.length === 0 ||
      rewardTokenAddresses.length === 0 ||
      !distributorResults
    ) {
      return [];
    }

    const result: RewardSlotData[] = [];

    for (let i = 0; i < distributorAddresses.length; i++) {
      const distAddr = distributorAddresses[i];

      /* Skip empty distributor slots (address(0)) */
      if (distAddr === zeroAddress) continue;

      const rTokenAddr = rewardTokenAddresses[i];
      const bps = bpsValues[i] ?? 0n;
      const meta = getRewardTokenMeta(rTokenAddr);

      const base = i * 5;
      const pending = (distributorResults[base]?.result as bigint | undefined) ?? 0n;
      const totalDist = (distributorResults[base + 1]?.result as bigint | undefined) ?? 0n;
      const totalDiv = (distributorResults[base + 2]?.result as bigint | undefined) ?? 0n;
      const holders = (distributorResults[base + 3]?.result as bigint | undefined) ?? 0n;
      const shareInfo = distributorResults[base + 4]?.result as
        | [bigint, bigint, bigint]
        | undefined;
      const totalRealised = shareInfo ? shareInfo[2] : 0n;

      result.push({
        slot: i,
        distributorAddress: distAddr,
        rewardTokenAddress: rTokenAddr,
        meta,
        pendingReward: pending,
        totalDistributed: totalDist,
        totalDividends: totalDiv,
        shareholderCount: holders,
        bps,
        totalRealised,
      });
    }

    return result;
  }, [distributorAddresses, rewardTokenAddresses, bpsValues, distributorResults]);

  /* ── Step 6: Claim functions ─────────────────────────────────────────── */

  const claimSlot = useCallback(
    (distributorAddress: Address) => {
      if (!account) return;
      writeContract({
        address: distributorAddress,
        abi: DISTRIBUTOR_ABI,
        functionName: "claimDividend",
        args: [account],
      });
    },
    [account, writeContract]
  );

  const claimAll = useCallback(() => {
    if (!account) return;
    /* Claim from each active distributor that has pending rewards */
    for (const slot of slots) {
      if (slot.pendingReward > 0n) {
        writeContract({
          address: slot.distributorAddress,
          abi: DISTRIBUTOR_ABI,
          functionName: "claimDividend",
          args: [account],
        });
      }
    }
  }, [account, slots, writeContract]);

  /* ── Derived values ──────────────────────────────────────────────────── */

  const totalPendingCount = slots.filter((s) => s.pendingReward > 0n).length;
  const activeSlotCount = slots.length;
  const emptySlotCount = Math.max(0, MAX_SLOTS - slotCount);

  const isLoading =
    isLoadingSlotCount ||
    isLoadingBalance ||
    isLoadingSlots ||
    isLoadingDistributors;

  const isClaiming = isWritePending || isConfirming;

  return {
    slots,
    slotCount,
    activeSlotCount,
    emptySlotCount,
    djnnBalance,
    totalPendingCount,
    isLoading,
    isClaiming,
    claimSlot,
    claimAll,
    txHash,
  };
}
